import { defineStore } from 'pinia'
import { useAuthStore } from '@sto/auth.store'

import * as AuctioneerService from '@ser/auctioneer'
import {
    assignLocation, getAccount, getAccountConfigurations,
    getConnections,
    getSaleSections,
    getSuggestedAccountCode, upsertAccountConfigurations,
    upsertConfiguration
} from "@ser/auctioneer";
import {show, users} from "@ser/auctioneer/api.js";
import role from "vue-multiselect";

export const useAuctioneerStore = defineStore('auctioneer', {
	
    /**
     *	The Store State
     */
    state: () => {
    	return {
            _auth_store: useAuthStore(),
        	_foo: null,
            _scope_auctioneer: null,
        }
    },
    
    /**
     * Store Getters
     */
    getters: {
        auth_store: state => state._auth_store,
    	foo: state => state._foo,
        scope_auctioneer: state => state._scope_auctioneer,
        scope_auctioneer_id: state => state._scope_auctioneer?.id || null,
    },
    
    /**
     *	Store Actions
     */
    actions: {

        setScopeAuctioneerId(auctioneer_id) {
            if (!auctioneer_id) {
                this._scope_auctioneer = null
            }

            if (Array.isArray(this.auth_store?.user_auctioneers) && this.auth_store.user_auctioneers.length) {
                this._scope_auctioneer = this.auth_store.user_auctioneers.find(auc => auc.id === auctioneer_id) ?? null
            }
        },

        clearScopeAuctioneerId() {
            this._scope_auctioneer = null
        },

        /**
         * get the sale codes for a given auctioneer
         * @param {int} auctioneer_id 
         * @returns 
         */
        async getSaleCodes(auctioneer_id, params) {
            return AuctioneerService.getSaleCodes(auctioneer_id, params)
        },

        /**
         * get the auctioneer's users
         * @param {int} auctioneer_id
         * @returns
         */
        async getUsers(auctioneer_id, params) {
            return AuctioneerService.users(auctioneer_id, params)
        },

        /**
         * Update auctioneer information.
         *
         * @param {string} auctioneer_id - The ID of the auctioneer to be updated.
         * @param {object} data - The updated data for the auctioneer.
         *
         * @returns {Promise<object>} - A Promise that resolves with the updated auctioneer object.
         */
        async update(auctioneer_id, data) {
            /**
             * NOTE:
             * this endpoint allows you update the auctioneer and the organisation in one.
             * because organisations and aucitoneers have properties with the same name, any data meant for the organisation must be prefixed with organisation_ and it's all resolved in the controller
             */
            return AuctioneerService.update(auctioneer_id, data)
        },

        /**
         * get the sale sections for a given auctioneer
         * @param {int} auctioneer_id
         * @returns
         */
        async getSaleSections(auctioneer_id, params) {
            return AuctioneerService.getSaleSections(auctioneer_id, params)
        },

        /**
         * get the defaul bidding interval rules for a given aucitoneer
         */
        async getBiddingIntervalRules(auctioneer_id, params) {
            return AuctioneerService.getBiddingIntervalRules(auctioneer_id, params)
        },

        /**
         * delete an auctioneer store
         */
        async deleteRule(auctioneer_id, rule_id) {
            return AuctioneerService.deleteRule(auctioneer_id, rule_id)
        },

        /**
         * create a rule
         */
        async createRule(auctioneer_id, payload) {
            return AuctioneerService.createRule(auctioneer_id, payload)
        },

        /**
         * get the configurations for a given auctioneer
         */
        async getConfigurations(auctioneer_id) {
            return AuctioneerService.getConfigurations(auctioneer_id)
        },

        /**
         * get a suggested account code for an auctioneer
         * @param auctioneer_id
         * @returns {Promise<obj>}
         */
        async getSuggestedAccountCode(auctioneer_id) {
            return AuctioneerService.getSuggestedAccountCode(auctioneer_id)
        },

        /**
         * grab a single aucitoneer by id
         * @param auctioneer_id
         * @returns {Promise<obj>}
         */
        async getAuctioneer(auctioneer_id) {
            return AuctioneerService.show(auctioneer_id)
        },

        /**
         * grab a single aucitoneer by id
         * @param auctioneer_id
         * @returns {Promise<obj>}
         */
        async publicShow(auctioneer_id) {
            return AuctioneerService.publicShow(auctioneer_id)
        },

        /**
         * Retrieve locations associated with a specific auctioneer.
         *
         * @param {string} auctioneer_id - The ID of the auctioneer for which locations should be retrieved.
         * @returns {Promise<object>} A Promise that resolves with an object containing the locations associated with the specified auctioneer.
         */
        async getAuctioneerLocations(auctioneer_id) {
            return AuctioneerService.locations(auctioneer_id)
        },

        // async getAccountLocations(auctioneer_id, account_id) {
        //     return AuctioneerService.getAccountLocations(auctioneer_id, account_id)
        // },

        async upsertConfiguration(auctioneer_id, payload) {
            return AuctioneerService.upsertConfiguration(auctioneer_id, payload)
        },

        async getConnections(auctioneer_id) {
            return AuctioneerService.getConnections(auctioneer_id)
        },

        /**
         * determine whether a specified user has all or some of the specified roles
         * @param user
         * @param organisation_id
         * @param roles
         * @param strict - if false checks for any role, if true checks for all roles
         * @returns {boolean}
         */
        userHasAuctioneerRole(user = null, organisation_id = null, roles = [], strict = false) {
            if (!user || !organisation_id || !Array.isArray(roles)) throw 'malformed role request'
            // find the scope organisation
            const organisation = user?.organisations.find(o => o.id === organisation_id)
            // if it doesn't exist we're falsey
            if (!organisation) return false

            return strict
                ? roles.every(role => organisation.user_roles.includes(role))
                : roles.some(role => organisation.user_roles.includes(role))
        },

        async createLocation(auctioneer_id = null, payload = {}) {
            return AuctioneerService.createLocation(auctioneer_id, payload)
        },

        async updateLocation(auctioneer_id = null, location_id = null, payload = {}) {
            return AuctioneerService.updateLocation(auctioneer_id, location_id, payload)
        },

        async assignLocation(auctioneer_id, location_id) {
            return AuctioneerService.assignLocation(auctioneer_id, location_id)
        },

        async getAssociatedOrganisations(auctioneer_id, params) {
            return AuctioneerService.getAssociatedOrganisations(auctioneer_id, params)
        },

        async getAccount(auctioneer_id, account_id) {
            return AuctioneerService.getAccount(auctioneer_id, account_id)
        },

        async getAccountConfigurations(auctioneer_id, account_id) {
            return AuctioneerService.getAccountConfigurations(auctioneer_id, account_id)
        },

        async upsertAccountConfigurations(auctioneer_id, account_id, data) {
            return AuctioneerService.upsertAccountConfigurations(auctioneer_id, account_id, data)
        }
    }
})